import { useState } from 'react';

interface DiscountModifierProps {
  discount: number;
  onDiscountChange: (value: number) => void;
  paygTotal: number;
}

export function DiscountModifier({ discount, onDiscountChange, paygTotal }: DiscountModifierProps) {
  const [open, setOpen] = useState(false);

  const discountedTotal = paygTotal * (1 - discount / 100);
  const saving = paygTotal - discountedTotal;

  function handleInput(val: string) {
    const parsed = parseFloat(val);
    if (isNaN(parsed)) return onDiscountChange(0);
    onDiscountChange(Math.min(70, Math.max(0, parsed)));
  }

  return (
    <div className="border border-outline-variant/30 rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-surface-container hover:bg-surface-container-high transition-colors"
      >
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">
          <span className="material-symbols-outlined text-[16px]">percent</span>
          Apply EA / CSP / MACC discount
          {discount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-[#107c10]/10 text-[#107c10] text-[9px] rounded font-bold border border-[#107c10]/30">
              {discount}% applied
            </span>
          )}
        </span>
        <span
          className="material-symbols-outlined text-[16px] text-outline transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          expand_more
        </span>
      </button>

      {open && (
        <div className="px-4 py-3 bg-surface-container/50 flex flex-col gap-3">
          <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>
            Have an EA or MCA agreement? The price above is PAYG (list rate). Apply your discount below to see your actual committed price.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-[11px] text-on-surface-variant w-full sm:w-[140px] sm:shrink-0">
              Negotiated discount (%)
            </label>
            <input
              type="range"
              min={0}
              max={70}
              step={1}
              value={discount}
              onChange={(e) => onDiscountChange(parseInt(e.target.value, 10))}
              className="flex-1 accent-primary"
            />
            <input
              type="number"
              min={0}
              max={70}
              value={discount}
              onChange={(e) => handleInput(e.target.value)}
              className="w-14 text-right bg-surface-container-high border border-outline-variant/40 rounded-sm px-2 py-1 text-[12px] text-on-surface"
            />
            <span className="text-[11px] text-on-surface-variant">%</span>
          </div>

          {discount > 0 && (
            <div className="flex flex-col gap-1 border-t border-outline-variant/20 pt-2.5">
              <div className="flex justify-between text-[12px]">
                <span className="text-on-surface-variant">PAYG estimate</span>
                <span className="font-mono text-on-surface">£{paygTotal.toFixed(2)}/mo</span>
              </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-[#107c10]">After {discount}% discount</span>
                <span className="font-mono font-bold text-[#107c10]">£{discountedTotal.toFixed(2)}/mo</span>
              </div>
              <div className="flex justify-between text-[11px] text-outline">
                <span>Saving</span>
                <span className="font-mono">£{saving.toFixed(2)}/mo</span>
              </div>
            </div>
          )}

          <p className="text-[10px] text-outline leading-relaxed">
            Retail (PAYG) prices shown. Discount is illustrative — verify with your Microsoft agreement.
          </p>
        </div>
      )}
    </div>
  );
}
